import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, increment, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useAnonymousLimits } from '../../hooks/useAnonymousLimits';
import SignupPrompt from '../Account/SignupPrompt';
import CoverRenderer from '../Storybook/CoverRenderer';
import FollowButton from '../Account/FollowButton';

const C = {
    primary: '#6a37d4', primaryLight: '#ae8dff', primaryContainer: '#ede8ff',
    surface: '#f3f6ff', surfaceLow: '#eaf1ff', surfaceHigh: '#d6e3f9',
    onBackground: '#252f3c', onSurfaceVariant: '#525c6a',
};
const HEADING = "'Epilogue', sans-serif";
const BODY = "'Plus Jakarta Sans', sans-serif";

const THEMES = {
    crisp:     { bg: '#ffffff', text: '#252f3c', label: 'White' },
    parchment: { bg: '#fbf6ea', text: '#2e2518', label: 'Parchment' },
    midnight:  { bg: '#181e28', text: '#f3f6ff', label: 'Midnight' },
} as const;

const SPACING = [1.7, 2.0, 2.3];

const tidyHtml = (html: string): string =>
    html
        .replace(/<p[^>]*>(\s|&nbsp;|<br[^>]*>)*<\/p>/gi, '')   // blank paragraphs
        .replace(/(<br[^>]*>\s*){2,}/gi, '<br>')                // doubled line breaks
        .replace(/(<p[^>]*>)(\s|&nbsp;)+/gi, '$1');             // leading spaces at the start of a paragraph

interface PublishedStory {
    storyId: string;
    userId: string;
    title: string;
    type: string;
    description: string;
    thumbnail?: any;
    content?: any;
    tags?: string[];  
    authorPseudonym: string;
    authorAvatar: string;
    reads: number;
    reactions: {
        heart: number;
        star: number;
        smile: number;
        party: number;
    };
}

const StoryReader: React.FC = () => {
    const { storyId } = useParams<{ storyId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const { hasReachedLimit, incrementStoryRead, getRemainingReads } = useAnonymousLimits();
    
    const [story, setStory] = useState<PublishedStory | null>(location.state?.story || null);
    const [currentChapter, setCurrentChapter] = useState(0);
    const [loading, setLoading] = useState(!story);
    const [showSignupPrompt, setShowSignupPrompt] = useState(false);
    const [readCounted, setReadCounted] = useState(false); // NEW: Track if we've counted this read
    const [copied, setCopied] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [spacingIdx, setSpacingIdx] = useState(1);

    const [liked, setLiked] = useState(false);
    const [heartBusy, setHeartBusy] = useState(false);

// Has this reader already hearted the story?
useEffect(() => {
    if (!currentUser || !storyId) { setLiked(false); return; }
    getDoc(doc(db, 'likes', `${storyId}_${currentUser.uid}`))
        .then(snap => setLiked(snap.exists()))
        .catch(console.error);
}, [currentUser, storyId]);

useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
}, []);

    const [fontSize, setFontSize] = useState(window.innerWidth <= 768 ? 18 : 21);
    const [theme, setTheme] = useState<'crisp' | 'parchment' | 'midnight'>('crisp');
    const [showChapters, setShowChapters] = useState(false);

    

    useEffect(() => {
        // Check anonymous user limits FIRST
          // DISABLED - Allow unlimited reading for everyone
    /*
        if (!currentUser) {
            const count = incrementStoryRead();
            
            if (count >= 10) {
                setShowSignupPrompt(true);
                return;
            }
        }
 */

        const loadStory = async () => {
            if (!story && storyId) {
                try {
                    const storyDoc = await getDoc(doc(db, 'publishedStories', storyId));
                    if (storyDoc.exists()) {
                        const storyData = storyDoc.data() as PublishedStory;
                        setStory(storyData);
                        
                        // ✅ FIX: Increment read count HERE, after successfully loading
                        // Read count tracking disabled
                        if (!readCounted) {
                            await updateDoc(doc(db, 'publishedStories', storyId), {
                                // reads: increment(1)
                            });
                            setReadCounted(true);
                            console.log('✅ Read count incremented for story:', storyId);
                        }
                    }
                } catch (error) {
                    console.error('Error loading story:', error);
                }
                setLoading(false);
            } else if (story && !readCounted) {
                // ✅ FIX: If story was passed via state, still increment the read count
                try {
                    if (storyId) {
                        await updateDoc(doc(db, 'publishedStories', storyId), {
                           // reads: increment(1)
                        });
                        setReadCounted(true);
                        console.log('✅ Read count incremented for story (from state):', storyId);
                    }
                } catch (error) {
                    console.error('Error incrementing read count:', error);
                }
            }
        };

        loadStory();
    }, [storyId, story, readCounted]); // Removed currentUser, incrementStoryRead

    // Scroll to top when chapter changes
useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
}, [currentChapter]);

    const handleClosePrompt = () => {
        if (hasReachedLimit) {
            navigate('/featured-stories');
        } else {
            setShowSignupPrompt(false);
        }
    };

    /*
    if (showSignupPrompt && hasReachedLimit) {
        return (
            <>
                <SignupPrompt
                    isOpen={true}
                    onClose={handleClosePrompt}
                    reason="read-limit"
                    remainingReads={getRemainingReads()}
                />
                <div style={{ 
                    filter: 'blur(8px)', 
                    pointerEvents: 'none',
                    minHeight: '100vh',
                    background: '#f5f5f5',
                    fontFamily: "'Inter', sans-serif"
                }}>
                    <div style={{
                        background: 'white',
                        padding: '20px 40px',
                        borderBottom: '2px solid #e0e0e0'
                    }}>
                        <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Story Title</h2>
                        <p style={{ margin: '5px 0 0', color: '#666' }}>by Author</p>
                    </div>
                    <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📚</div>
                        <h1 style={{ fontSize: '2rem', marginBottom: '20px' }}>Amazing Story</h1>
                        <p>Story content goes here...</p>
                    </div>
                </div>
            </>
        );
    }
 */

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                fontSize: '2rem'
            }}>
                Loading story... 📖
            </div>
        );
    }

    if (!story) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                padding: '20px'
            }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📚</div>
                <h2>Story not found</h2>
                <button
                    onClick={() => navigate('/featured-stories')}
                    style={{
                        marginTop: '20px',
                        padding: '12px 24px',
                        background: '#6C5CE7',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        cursor: 'pointer'
                    }}
                >
                    Back to Stories
                </button>
            </div>
        );
    }

    const chapters = story.content?.chapters || [];
    const isOnCover = currentChapter === -1;
    const currentChapterData = chapters[currentChapter];

    const chapterWordCount = (() => {
        if (!currentChapterData) return 0;
        const raw = currentChapterData.blocks
            ? currentChapterData.blocks.filter((b: any) => b.type === 'text').map((b: any) => b.content || '').join(' ')
            : currentChapterData.content || '';
        return raw.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
    })();
    const readMinutes = Math.max(1, Math.ceil(chapterWordCount / 200));

    const authorLink = (
        <button
            onClick={() => navigate(`/writer/${story.userId}`)}
            style={{
                background: 'linear-gradient(transparent 58%, #fed01b 58%)',
                color: C.onBackground,
                border: 'none',
                borderRadius: 0,
                padding: '0 4px',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                fontWeight: 800,
                textTransform: 'none',
                letterSpacing: 'normal',
                cursor: 'pointer'
            }}
        >
            {story.authorAvatar} {story.authorPseudonym}
        </button>
    );

    const handleCopyLink = async () => {
        const url = `${window.location.origin}/read-story/${storyId}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            window.prompt('Copy this link:', url);   // fallback if the browser blocks clipboard access
        }
    };
    
    const copyLinkBtn = (bg: string) => (
        <button
            onClick={handleCopyLink}
            style={{ minHeight: '44px', padding: '10px 24px', borderRadius: '9999px', border: 'none', background: bg, color: C.primary, fontFamily: HEADING, fontSize: '14px', fontWeight: 800, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
        >
            {copied ? '✓ Link copied!' : '🔗 Copy link'}
        </button>
    );

    const handleHeart = async () => {
        if (!currentUser) { setShowSignupPrompt(true); return; }
        if (!storyId || currentUser.uid === story.userId || heartBusy) return;
        setHeartBusy(true);
        const likeRef = doc(db, 'likes', `${storyId}_${currentUser.uid}`);
        const storyRef = doc(db, 'publishedStories', storyId);
        const next = !liked;
        try {
            if (next) {
                await setDoc(likeRef, { storyId, userId: currentUser.uid, likedAt: new Date() });
                await updateDoc(storyRef, { 'reactions.heart': increment(1) });
            } else {
                await deleteDoc(likeRef);
                await updateDoc(storyRef, { 'reactions.heart': increment(-1) });
            }
            setLiked(next);
            setStory(prev => prev ? {
                ...prev,
                reactions: { ...prev.reactions, heart: Math.max(0, (prev.reactions?.heart ?? 0) + (next ? 1 : -1)) }
            } : prev);
        } catch (err) {
            console.error('Heart failed:', err);
        } finally {
            setHeartBusy(false);
        }
    };
    
    const t = THEMES[theme];
    const isLastChapter = chapters.length > 0 && currentChapter === chapters.length - 1;
    const progress = chapters.length > 0 && !isOnCover ? ((currentChapter + 1) / chapters.length) * 100 : 0;
    const chapterLabel = isOnCover
        ? 'Cover'
        : `Ch. ${currentChapter + 1}${currentChapterData?.title ? `: ${currentChapterData.title}` : ''}`;
    const chapterItems = [
        { idx: -1, label: 'Cover' },
        ...chapters.map((ch: any, i: number) => ({ idx: i, label: `Chapter ${i + 1}${ch.title ? `: ${ch.title}` : ''}` })),
    ];

    const pillBtn: React.CSSProperties = {
        minHeight: '40px', border: 'none', borderRadius: '9999px', background: '#fff',
        fontFamily: HEADING, fontWeight: 800, fontSize: '0.8rem', color: C.onBackground,
        cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
    };

    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: BODY }}>

            {/* Toolbar */}
            <div style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(243,246,255,0.92)', backdropFilter: 'blur(12px)', boxShadow: '0 1px 8px rgba(37,47,60,0.06)' }}>
                {!isOnCover && chapters.length > 0 && (
                    <div style={{ height: '4px', background: C.surfaceHigh }}>
                        <div style={{ height: '100%', width: `${progress}%`, background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`, transition: 'width 0.3s ease' }} />
                    </div>
                )}
                <div style={{ maxWidth: '1560px', margin: '0 auto', padding: isMobile ? '10px 12px' : '12px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate('/featured-stories')} style={{ ...pillBtn, padding: '8px 18px' }}>
                        ← Back
                    </button>

                    {/* Chapter picker */}
                    <div style={{ position: 'relative' }}>
                        <button onClick={() => setShowChapters(o => !o)} style={{ ...pillBtn, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', maxWidth: isMobile ? '180px' : '320px' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#006947', flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chapterLabel}</span>
                            <span style={{ color: C.onSurfaceVariant }}>▾</span>
                        </button>
                        {showChapters && (
                            <>
                                <div onClick={() => setShowChapters(false)} style={{ position: 'fixed', inset: 0, zIndex: 45 }} />
                                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: 'min(280px, 80vw)', maxHeight: '320px', overflowY: 'auto', background: '#fff', borderRadius: '20px', boxShadow: '0 12px 32px rgba(0,0,0,0.18)', padding: '8px', zIndex: 50 }}>
                                    {chapterItems.map(item => (
                                        <button
                                            key={item.idx}
                                            onClick={() => { setCurrentChapter(item.idx); setShowChapters(false); }}
                                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderRadius: '12px', background: item.idx === currentChapter ? C.primaryContainer : 'transparent', color: item.idx === currentChapter ? C.primary : C.onBackground, fontFamily: BODY, fontSize: '0.88rem', fontWeight: item.idx === currentChapter ? 800 : 600, cursor: 'pointer' }}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Reading controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '2px', background: '#fff', borderRadius: '9999px', padding: '2px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                            <button onClick={() => setFontSize(s => Math.max(15, s - 1.5))} aria-label="Smaller text" style={{ ...pillBtn, width: '38px', boxShadow: 'none', fontSize: '0.75rem' }}>A-</button>
                            <button onClick={() => setFontSize(s => Math.min(26, s + 1.5))} aria-label="Larger text" style={{ ...pillBtn, width: '38px', boxShadow: 'none', fontSize: '0.95rem' }}>A+</button>
                            <button
    onClick={() => setSpacingIdx(i => (i + 1) % SPACING.length)}
    aria-label="Line spacing"
    title="Line spacing"
    style={{ ...pillBtn, width: '38px', boxShadow: 'none', fontSize: '1rem' }}
>
↕ {spacingIdx + 1}
</button>
                        </div>
                        <div style={{ display: 'flex', gap: '4px', background: '#fff', borderRadius: '9999px', padding: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                            {(Object.keys(THEMES) as Array<keyof typeof THEMES>).map(k => (
                                <button
                                    key={k}
                                    onClick={() => setTheme(k)}
                                    aria-label={`${THEMES[k].label} background`}
                                    title={THEMES[k].label}
                                    style={{ width: '30px', height: '30px', borderRadius: '50%', background: THEMES[k].bg, cursor: 'pointer', border: theme === k ? `3px solid ${C.primary}` : '2px solid #d6e3f9' }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div style={{ maxWidth: '1560px', margin: isMobile ? '20px auto' : '24px auto', padding: isMobile ? '0 12px' : '0 32px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 300px', gap: '24px', alignItems: 'start' }}>

                {/* Main column */}
                <div>
                    {isOnCover ? (
                        <div style={{ background: '#fff', borderRadius: '28px', padding: isMobile ? '36px 20px' : '56px 40px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            {story.thumbnail && (
                                <div style={{ width: isMobile ? '200px' : '250px', margin: '0 auto 32px' }}>
                                    <CoverRenderer
                                        coverData={typeof story.thumbnail === 'string' ? JSON.parse(story.thumbnail) : story.thumbnail}
                                        borderRadius={12}
                                        boxShadow="0 8px 32px rgba(0,0,0,0.15)"
                                    />
                                </div>
                            )}
                            <h1 style={{ fontFamily: HEADING, fontSize: isMobile ? '1.9rem' : '2.5rem', fontWeight: 900, margin: '0 0 16px', color: C.onBackground, lineHeight: 1.2 }}>
                                {story.title}
                            </h1>
                            <p style={{ fontSize: '1.1rem', color: C.onSurfaceVariant, margin: '0 0 24px', fontWeight: 600 }}>
                                by {authorLink}
                            </p>
                            {story.description && (
                                <p style={{ fontSize: '1rem', color: C.onSurfaceVariant, margin: '0 0 36px', lineHeight: 1.65 }}>
                                    {story.description}
                                </p>
                            )}
                            <button
                                onClick={() => setCurrentChapter(0)}
                                style={{ minHeight: '52px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', border: 'none', padding: '14px 44px', borderRadius: '9999px', fontFamily: HEADING, fontSize: '1.05rem', fontWeight: 800, cursor: 'pointer', boxShadow: `0 4px 16px ${C.primary}40` }}
                            >
                                Start Reading →
                            </button>
                        </div>
                    ) : (
                        <article style={{ background: t.bg, color: t.text, borderRadius: '28px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'background 0.25s, color 0.25s' }}>

    {/* Banner */}
<div style={{
    position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(135deg, #ede8ff 0%, #f7f3ff 60%, #fff8dc 100%)',
    padding: isMobile ? '20px 20px 22px' : '28px 64px 30px',
    borderBottom: '1px solid #e4dcff'
}}>
    <div style={{ position: 'absolute', right: -60, top: -80, width: 240, height: 240, borderRadius: '50%', background: 'rgba(174,141,255,0.25)', filter: 'blur(40px)', pointerEvents: 'none' }} />

    <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
            {story.tags?.[0] && (
                <span style={{ background: '#fed01b', color: '#433500', padding: '4px 12px', borderRadius: '9999px', fontSize: '0.72rem', fontWeight: 800 }}>
                    {story.tags[0].replace(/[^\w\s-]/g, '').trim()}
                </span>
            )}
            <span style={{ background: '#d1fae5', color: '#006947', padding: '4px 12px', borderRadius: '9999px', fontSize: '0.72rem', fontWeight: 800 }}>
                Chapter {currentChapter + 1} of {chapters.length}
            </span>
            <span style={{ color: C.onSurfaceVariant, fontSize: '0.78rem', fontWeight: 600 }}>🕒 {readMinutes} min read</span>
        </div>

        <h1 style={{ fontFamily: HEADING, fontSize: isMobile ? '1.8rem' : '2.8rem', fontWeight: 900, margin: 0, lineHeight: 1.15, color: C.onBackground, textAlign: 'left' }}>
            {currentChapterData?.title || story.title}
        </h1>

        <p style={{ margin: '10px 0 0', fontSize: '0.95rem', fontWeight: 600, color: C.onSurfaceVariant }}>
            {currentChapterData?.title && <>From <strong style={{ color: C.onBackground }}>{story.title}</strong> • </>}by {authorLink}
        </p>
    </div>
</div>

    {/* Text */}
    <div style={{ padding: isMobile ? '28px 20px 36px' : '44px 64px 56px' }}>
    <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: `${fontSize}px`, lineHeight: SPACING[spacingIdx], color: t.text, transition: 'font-size 0.2s' }}>
    <style>{`
    .story-content, .story-content * { line-height: inherit !important; font-family: inherit !important; }
    .story-content p { margin: 0; text-indent: 1.5em; }
    .story-content p:empty { display: none; }
    .story-content p[style*="text-align: center"] { text-indent: 0; margin: 0.6em 0; }
    .story-content img { max-width: 100%; }
    .story-content > div:first-child > p:first-child { text-indent: 0; }
    .story-content > div:first-child > p:first-child::first-line {
        font-variant: small-caps; letter-spacing: 0.04em;
    }
`}</style>
            <div className="story-content">
                {currentChapterData?.blocks ? (
                    currentChapterData.blocks.map((block: any) => {
                        if (block.type === 'text') return (
                            <div key={block.id} dangerouslySetInnerHTML={{ __html: tidyHtml(block.content || '') }} />
                        );
                        if (block.type === 'image' && block.imageUrl) return (
                            <div key={block.id} style={{ textAlign: 'center', margin: '24px 0' }}>
                                <img src={block.imageUrl} alt={block.caption || ''} style={{ maxWidth: '100%', borderRadius: 12 }} />
                                {block.caption && <p style={{ fontSize: '0.85rem', opacity: 0.75, fontStyle: 'italic', marginTop: 8 }}>{block.caption}</p>}
                            </div>
                        );
                        return null;
                    })
                ) : (
                    <div dangerouslySetInnerHTML={{ __html: tidyHtml(currentChapterData?.content || '') }} />
                )}
            </div>
        </div>
    </div>
</article>
                    )}

                    {/* End of story */}
                    {isLastChapter && (
    <div style={{ marginTop: '20px', background: `linear-gradient(135deg, ${C.primaryContainer}, #d1fae5)`, borderRadius: '24px', padding: isMobile ? '24px 20px' : '28px 32px', textAlign: 'center' }}>
        <h3 style={{ fontFamily: HEADING, fontWeight: 900, fontSize: '1.5rem', margin: '0 0 8px', color: C.onBackground }}>
            The End!
        </h3>
        <p style={{ margin: '0 auto', maxWidth: '520px', color: C.onSurfaceVariant, fontWeight: 500, lineHeight: 1.5, fontSize: '0.95rem' }}>
            Did you enjoy {story.title}? Give it a heart and follow @{story.authorPseudonym} to find more of their stories.
        </p>

        {/* Heart: its own row */}
        {currentUser?.uid !== story.userId && (
            <div style={{ margin: '18px 0 16px' }}>
                <button
                    onClick={handleHeart}
                    disabled={heartBusy}
                    aria-pressed={liked}
                    style={{ minHeight: '48px', padding: '10px 28px', borderRadius: '9999px', border: 'none', background: liked ? '#fee2e2' : '#fff', color: '#ef4444', fontFamily: HEADING, fontSize: '1rem', fontWeight: 800, cursor: heartBusy ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                >
                    {liked ? '❤️' : '🤍'} {story.reactions?.heart ?? 0}
                </button>
            </div>
        )}

        {/* Follow + copy link + explore */}
<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: currentUser?.uid === story.userId ? '18px' : 0 }}>
    <FollowButton targetUserId={story.userId} />
    {copyLinkBtn('#fff')}
    <button
    onClick={() => navigate('/featured-stories')}
    style={{ minHeight: '44px', padding: '10px 24px', borderRadius: '9999px', border: 'none', background: 'linear-gradient(135deg, #6a37d4, #ae8dff)', color: 'white', fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '14px', fontWeight: 800, cursor: 'pointer' }}
>
    Explore more stories
</button>
</div>
       
    </div>
)}

                    {/* Prev / Next */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', gap: '16px' }}>
                        <button
                            onClick={() => setCurrentChapter(prev => Math.max(-1, prev - 1))}
                            disabled={isOnCover}
                            style={{ flex: 1, minHeight: '52px', padding: '14px', background: isOnCover ? C.surfaceHigh : '#fff', border: 'none', borderRadius: '9999px', fontFamily: HEADING, fontSize: '1rem', fontWeight: 800, cursor: isOnCover ? 'not-allowed' : 'pointer', color: isOnCover ? C.onSurfaceVariant : C.primary, opacity: isOnCover ? 0.6 : 1, boxShadow: isOnCover ? 'none' : '0 2px 8px rgba(0,0,0,0.06)' }}
                        >
                            ← Previous
                        </button>
                        <button
                            onClick={() => {
                                if (currentChapter < chapters.length - 1) setCurrentChapter(prev => prev + 1);
                                else navigate('/featured-stories');
                            }}
                            style={{ flex: 1, minHeight: '52px', padding: '14px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, border: 'none', borderRadius: '9999px', fontFamily: HEADING, fontSize: '1rem', fontWeight: 800, cursor: 'pointer', color: '#fff', boxShadow: `0 4px 16px ${C.primary}40` }}
                        >
                            {currentChapter < chapters.length - 1 ? 'Next →' : 'Finish'}
                        </button>
                    </div>
                </div>

                {/* Sidebar: author card */}
                <aside style={{ position: isMobile ? 'static' : 'sticky', top: '96px' }}>
                    <div style={{ background: '#fff', borderRadius: '24px', padding: '22px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div
                            onClick={() => navigate(`/writer/${story.userId}`)}
                            style={{ display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer' }}
                        >
                            <div style={{ width: 56, height: 56, borderRadius: '18px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.9rem', flexShrink: 0 }}>
                                {story.authorAvatar}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontFamily: HEADING, fontWeight: 900, fontSize: '1rem', color: C.onBackground, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    @{story.authorPseudonym}
                                </div>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.primary }}>See all stories →</div>
                            </div>
                        </div>
                        <FollowButton targetUserId={story.userId} />
                        {copyLinkBtn(C.primaryContainer)}
                    </div>
                </aside>
            </div>
            <SignupPrompt isOpen={showSignupPrompt} onClose={() => setShowSignupPrompt(false)} reason="like" />
        </div>
        
    );
};

export default StoryReader;